import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ContactModal from '../components/ContactModal';
import { Button } from '../components/ui/button';
import { CheckCircle, ArrowRight, Shield, FileCheck, Search, Clock, Award, Users } from 'lucide-react';
import TestimonialsSlider from '../components/TestimonialsSlider';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

const TrademarkRegistration = () => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const pricingPlans = [
    { name: 'Basic', price: 45, features: ['Case Review', 'Case Filing', 'Basic Search', 'State E-Filing', 'Trademark Secured'] },
    { name: 'Deluxe', price: 145, features: ['Case Review', 'Case Filing', 'Basic Search', 'State E-Filing', 'Trademark Secured', 'Direct Hit Search', 'Dedicated Case Manager'] },
    { name: 'Premium', price: 299, popular: true, features: ['Case Review (by a TM Expert)', 'Basic Free Trademark Search', 'Trademark Monitoring Services (3 Months)', 'Trademark Secured', 'Refusal Risk Meter', 'Case filing service with complete paperwork'] },
    { name: 'Elite', price: 599, features: ['Case Review & Filing (U.S Registered IP Attorney)', 'Assigned Case Filing Officer', 'Private Registration', 'Trademark Secured', 'Federal Search & Clearance Report (All States)', 'Federal E-Filing (USPTO)', 'Cease & Desist Letter', 'Same Day Filing', 'Trademark Monitoring (1 Year)', 'Direct Hit Search', 'Dedicated Case Manager', '24/7 Secure Online Access', '100% Approval Guarantee'] }
  ];

  const faqs = [
    { q: 'How long does trademark registration take?', a: 'The trademark registration process typically takes 8-12 months from filing to registration. We process and file your application within 24 hours.' },
    { q: 'What can be trademarked?', a: 'You can trademark brand names, logos, slogans, and any unique identifier that distinguishes your goods or services from competitors.' },
    { q: 'Do I need a lawyer to register a trademark?', a: 'While not required, professional assistance significantly increases your chances of success. Our experienced team handles all complexities.' },
    { q: 'What is the government filing fee?', a: 'USPTO government filing fees range from $250-$350 per class, which is separate from our service fees.' },
    { q: 'Can my trademark be rejected?', a: 'Yes, but we handle office actions and work to address any USPTO concerns to maximize your approval chances.' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar onOpenModal={() => setIsModalOpen(true)} />
      
      {/* About the Service */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-emerald-50 via-teal-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center animate-fade-in">
            <h1 className="text-5xl font-bold text-gray-800 mb-6">Trademark Registration Services</h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Protect your brand identity with our comprehensive trademark registration services. Starting at just $45, we make brand protection accessible, efficient, and hassle-free for businesses of all sizes.
            </p>
            <Button size="lg" onClick={() => setIsModalOpen(true)} className="bg-brand-gradient text-white hover:opacity-90 group shadow-lg hover:shadow-xl transition-all font-bold text-lg px-8 py-6">
              Get Started Today
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={24} />
            </Button>
          </div>
        </div>
      </section>

      {/* 3-Step Process */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-gray-800 mb-12 animate-fade-in">Our Simple <span className="text-brand-primary">3-Step Process</span></h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              { icon: FileCheck, title: 'Submit Your Details', desc: 'Fill out our simple form with your brand information and trademark details.' },
              { icon: Search, title: 'We Review & Search', desc: 'Our experts conduct a thorough search and review your application for conflicts.' },
              { icon: Shield, title: 'File & Protect', desc: 'We file your trademark with USPTO and monitor the process until registration.' }
            ].map((step, idx) => {
              const Icon = step.icon;
              return (
                <div key={idx} className="text-center animate-scale-in" style={{animationDelay: `${idx * 0.1}s`}}>
                  <div className="bg-brand-gradient w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse-glow">
                    <Icon className="text-white" size={32} />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-3">{step.title}</h3>
                  <p className="text-base text-gray-600 leading-relaxed">{step.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why You Need It */}
      <section className="py-20 bg-gradient-to-br from-emerald-50 via-teal-50 to-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-gray-800 mb-12 animate-fade-in">Why You Need <span className="text-brand-primary">Trademark Registration</span></h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              { title: 'Legal Protection', desc: 'Gain exclusive legal rights to your brand identity nationwide and prevent unauthorized use.' },
              { title: 'Brand Recognition', desc: 'Build customer trust and establish your brand as a credible, professional entity.' },
              { title: 'Competitive Edge', desc: 'Stand out in the marketplace and prevent competitors from using similar marks.' },
              { title: 'Asset Value', desc: 'Your trademark becomes a valuable business asset that can appreciate over time.' },
              { title: 'Legal Recourse', desc: 'Take legal action against infringers and protect your brand investment.' },
              { title: 'Nationwide Rights', desc: 'Secure protection across all 50 states with potential for global expansion.' }
            ].map((item, idx) => (
              <div key={idx} className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all animate-scale-in" style={{animationDelay: `${idx * 0.1}s`}}>
                <h3 className="text-xl font-bold text-gray-800 mb-3">{item.title}</h3>
                <p className="text-base text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 animate-fade-in">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Affordable Pricing for Every Business</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">Choose the package that best fits your needs and budget</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {pricingPlans.map((plan, idx) => (
              <div key={plan.name} className={`rounded-2xl p-8 transition-all duration-300 hover:scale-105 flex flex-col h-full animate-scale-in ${plan.popular ? 'bg-gradient-to-br from-emerald-600 to-teal-500 text-white shadow-2xl ring-4 ring-emerald-200' : 'bg-white shadow-lg hover:shadow-xl border border-emerald-100'}`} style={{animationDelay: `${idx * 0.1}s`}}>
                <div className="text-center mb-6">
                  <h3 className={`text-2xl font-bold mb-2 ${plan.popular ? 'text-white' : 'text-gray-800'}`}>{plan.name}</h3>
                  <div className="flex items-baseline justify-center">
                    <span className={`text-5xl font-bold ${plan.popular ? 'text-white' : 'text-brand-primary'}`}>${plan.price}</span>
                  </div>
                  <p className={`text-sm mt-2 ${plan.popular ? 'text-emerald-50' : 'text-gray-500'}`}>+ Govt. filing fee</p>
                </div>
                <div className="flex-grow mb-6 overflow-y-auto max-h-64 pr-2 scrollbar-thin">
                  <div className="space-y-3">
                    {plan.features.map((feature, i) => (
                      <div key={i} className="flex items-start space-x-2">
                        <CheckCircle className={`flex-shrink-0 mt-0.5 ${plan.popular ? 'text-emerald-100' : 'text-brand-primary'}`} size={20} />
                        <span className={`text-sm ${plan.popular ? 'text-white' : 'text-gray-600'}`}>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <Button onClick={() => setIsModalOpen(true)} className={`w-full mt-auto ${plan.popular ? 'bg-white text-brand-primary hover:bg-gray-50 border-2 border-brand-primary' : 'bg-brand-gradient text-white hover:opacity-90'}`}>Choose Plan</Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gradient-to-br from-emerald-50 via-teal-50 to-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-gray-800 mb-16 animate-fade-in">Why Choose <span className="text-brand-primary">Trademark Law Associates</span></h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {[
              { icon: Shield, title: 'Expert Team', desc: 'Experienced IP professionals handle your trademark with care' },
              { icon: Clock, title: '24-Hour Filing', desc: 'Fast processing and filing within one business day' },
              { icon: Award, title: '98% Success Rate', desc: 'High approval rate with expert application preparation' },
              { icon: Users, title: '24/7 Support', desc: 'Dedicated customer support at every step' }
            ].map((item, idx) => {
              const Icon = item.icon;
              return (
                <div key={idx} className="text-center animate-scale-in" style={{animationDelay: `${idx * 0.1}s`}}>
                  <div className="bg-white w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <Icon className="text-brand-primary" size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">{item.title}</h3>
                  <p className="text-base text-gray-600">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div className="animate-fade-in">
              <img src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800" alt="Benefits" className="rounded-2xl shadow-xl hover-zoom" />
            </div>
            <div className="animate-slide-up">
              <h2 className="text-4xl font-bold text-gray-800 mb-6">Key Benefits of Our Service</h2>
              <div className="space-y-4">
                {[
                  'Comprehensive trademark search to avoid conflicts',
                  'Professional application preparation by experts',
                  'USPTO filing and monitoring throughout the process',
                  'Affordable pricing starting at just $45',
                  'Fast turnaround with 24-hour processing available',
                  '100% satisfaction guarantee on all services'
                ].map((benefit, idx) => (
                  <div key={idx} className="flex items-start space-x-3">
                    <CheckCircle className="text-brand-primary flex-shrink-0 mt-1" size={24} />
                    <p className="text-lg text-gray-700">{benefit}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <TestimonialsSlider />

      {/* FAQs */}
      <section className="py-20 bg-gradient-to-br from-emerald-50 via-teal-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-center text-gray-800 mb-12 animate-fade-in">Frequently Asked <span className="text-brand-primary">Questions</span></h2>
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, idx) => (
                  <AccordionItem key={idx} value={`item-${idx}`}>
                    <AccordionTrigger className="text-left font-semibold text-gray-800">{faq.q}</AccordionTrigger>
                    <AccordionContent className="text-base text-gray-600">{faq.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-brand-gradient">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center animate-fade-in">
            <h2 className="text-4xl font-bold text-white mb-6">Ready to Protect Your Brand?</h2>
            <p className="text-xl text-emerald-50 mb-8">Start your trademark registration today and secure your business future</p>
            <Button size="lg" onClick={() => navigate('/')} className="bg-white text-brand-primary hover:bg-gray-50 shadow-xl border-2 border-brand-primary">
              Get Started Now
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
      <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default TrademarkRegistration;